import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { submitDrafts, type AppDrafts, type ImageType } from '@/api/play';
import { playLocaleName } from '@/lib/playLocales';
import { chunk } from '@/lib/chunk';
import { Modal } from '../Modal';
import { Button } from '../Button';

const TYPE_LABEL: Record<ImageType, string> = {
  icon: 'icon',
  featureGraphic: 'feature graphic',
  phoneScreenshots: 'screenshots',
};

// Locales per submit request. Each batch is one Play edit (one validate+commit),
// so this bounds per-request time and subrequest count, and isolates failures —
// a bad locale only fails its own batch instead of the whole submission.
const BATCH_SIZE = 5;

type Failure = { locales: string[]; error: string };
type Phase = 'select' | 'submitting' | 'done';

export function SubmitDraftsModal({ appId, drafts, onClose }: {
  appId: number; drafts: AppDrafts; onClose: () => void;
}) {
  const qc = useQueryClient();

  const rows = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const l of drafts.listings) map.set(l.language, ['text']);
    for (const d of drafts.images) {
      const parts = map.get(d.language) ?? [];
      parts.push(d.imageType === 'phoneScreenshots' ? `screenshots (${d.items.length})` : TYPE_LABEL[d.imageType]);
      map.set(d.language, parts);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [drafts]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(rows.map(([lang]) => lang)));
  const [phase, setPhase] = useState<Phase>('select');
  const [total, setTotal] = useState(0);
  const [doneLocales, setDoneLocales] = useState<string[]>([]);
  const [failures, setFailures] = useState<Failure[]>([]);
  const [activeBatch, setActiveBatch] = useState<string[]>([]);

  const toggle = (lang: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(lang)) next.delete(lang);
    else next.add(lang);
    return next;
  });

  // Submit the locales in sequential batches. A failed batch is recorded and the
  // run continues — already-committed batches stay submitted (their drafts are
  // cleared server-side), so only the failures need a retry.
  const run = async (locales: string[]) => {
    setPhase('submitting');
    setTotal(locales.length);
    setDoneLocales([]);
    setFailures([]);
    const submitted: string[] = [];
    const failed: Failure[] = [];
    for (const batch of chunk(locales, BATCH_SIZE)) {
      setActiveBatch(batch);
      try {
        await submitDrafts(appId, batch);
        submitted.push(...batch);
        setDoneLocales([...submitted]);
        // Only refresh drafts here (D1, no Play edit). Do NOT refresh the live
        // snapshot per batch — it opens a second Play edit concurrently with the
        // in-flight submit edit, which Play invalidates ("This Edit has been
        // deleted"), killing the submit. Snapshot is refreshed once at the end.
        qc.invalidateQueries({ queryKey: ['play-drafts', appId] });
      } catch (e: any) {
        failed.push({ locales: batch, error: e?.message ?? 'Submit failed' });
        setFailures([...failed]);
      }
    }
    setActiveBatch([]);
    // Now that no submit edit is open, refresh the live snapshot once.
    qc.invalidateQueries({ queryKey: ['play-snapshot', appId] });
    setPhase('done');
  };

  const failedLocales = failures.flatMap((f) => f.locales);
  const settled = doneLocales.length + failedLocales.length;
  const progress = total === 0 ? 0 : Math.round((settled / total) * 100);

  // Block close mid-flight so the progress/result state can't be lost.
  const closable = phase !== 'submitting';

  return (
    <Modal isOpen onClose={closable ? onClose : () => {}} title="Submit to review" size="md">
      {phase === 'select' && (
        <>
          <p className="text-sm text-content-muted mb-3">
            Select the locales to send. Submitting commits them to Google Play — they go to
            Google review and publish after approval (usually hours to a few days). Locales are
            sent in batches of {BATCH_SIZE}; a failed batch won’t block the rest.
          </p>
          <div className="border border-border rounded-md divide-y divide-border mb-4 max-h-72 overflow-y-auto">
            {rows.map(([lang, parts]) => (
              <label key={lang} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-surface-muted">
                <input type="checkbox" checked={selected.has(lang)} onChange={() => toggle(lang)} />
                <span className="text-sm text-content">{lang}</span>
                <span className="text-xs text-content-muted">{playLocaleName(lang) ?? ''}</span>
                <span className="ml-auto text-xs text-content-muted">{parts.join(' · ')}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={selected.size === 0} onClick={() => run(Array.from(selected))}>
              {selected.size > 1 ? `Submit ${selected.size} locales` : 'Submit'}
            </Button>
          </div>
        </>
      )}

      {phase === 'submitting' && (
        <div className="py-2">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-content">Submitting to review…</span>
            <span className="text-content-muted">{settled} / {total}</span>
          </div>
          <div className="h-2 rounded-full bg-surface-muted overflow-hidden mb-3">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-content-muted break-words">
            Current batch: {activeBatch.join(', ')}
          </p>
          {failedLocales.length > 0 && (
            <p className="text-xs text-red-400 mt-2">
              {failedLocales.length} locale(s) failed so far — details when it finishes.
            </p>
          )}
        </div>
      )}

      {phase === 'done' && (
        <div className="py-1">
          {doneLocales.length > 0 && (
            <p className="text-sm text-green-400 mb-3">
              ✓ Submitted {doneLocales.length} locale(s) to review:{' '}
              <span className="text-content-muted break-words">{doneLocales.join(', ')}</span>
            </p>
          )}
          {failures.length === 0 ? (
            <p className="text-sm text-content-muted">All selected locales were submitted.</p>
          ) : (
            <div className="mb-1">
              <p className="text-sm text-red-400 mb-2">
                {failedLocales.length} locale(s) failed — fix and retry; nothing was lost, their drafts are intact:
              </p>
              <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                {failures.map((f, i) => (
                  <div key={i} className="px-3 py-2">
                    <p className="text-xs text-content">{f.locales.join(', ')}</p>
                    <p className="text-xs text-red-400 mt-0.5 break-words">{f.error}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            {failures.length > 0 && (
              <Button variant="secondary" onClick={() => run(failedLocales)}>
                Retry failed ({failedLocales.length})
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
